import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    TextField,
    Button,
    MenuItem,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Autocomplete,
    Chip,
    Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { 
    getFirestore, 
    collection, 
    getDocs, 
    query, 
    orderBy as firestoreOrderBy,
    setDoc,
    doc,
    runTransaction
} from 'firebase/firestore';

const firestore = getFirestore();

function AddTrabajo() {
    const [loading, setLoading] = useState(false);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [obras, setObras] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [materiales, setMateriales] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    const [formData, setFormData] = useState({
        fecha: moment(),
        cuadrilla: '',
        obra: null,
        observaciones: ''
    });
    
    const [serviciosSeleccionados, setServiciosSeleccionados] = useState([]);
    const [materialesSeleccionados, setMaterialesSeleccionados] = useState([]);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                fetchCuadrillas(),
                fetchObras(),
                fetchServicios(),
                fetchMateriales()
            ]);
        } catch (error) {
            console.error('Error cargando datos:', error);
            showSnackbar('Error al cargar los datos iniciales', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchCuadrillas = async () => {
        const q = query(collection(firestore, 'cuadrillas_huawei'), firestoreOrderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const cuadrillasData = [];
        querySnapshot.forEach((docSnap) => {
            cuadrillasData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        // Filtrar solo cuadrillas (no depósitos)
        setCuadrillas(cuadrillasData.filter(c => c.tipo !== 'deposito'));
    };

    const fetchObras = async () => {
        const q = query(collection(firestore, 'obras_huawei'), firestoreOrderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const obrasData = [];
        querySnapshot.forEach((docSnap) => {
            obrasData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setObras(obrasData);
    };

    const fetchServicios = async () => {
        const q = query(collection(firestore, 'precios_huawei'), firestoreOrderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const serviciosData = [];
        querySnapshot.forEach((docSnap) => {
            serviciosData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setServicios(serviciosData);
    };

    const fetchMateriales = async () => {
        const q = query(collection(firestore, 'materiales_huawei'), firestoreOrderBy('id', 'asc'));
        const querySnapshot = await getDocs(q);
        const materialesData = [];
        querySnapshot.forEach((docSnap) => {
            materialesData.push({ ...docSnap.data(), docId: docSnap.id });
        });
        setMateriales(materialesData);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (newDate) => {
        setFormData(prev => ({ ...prev, fecha: newDate }));
    };

    const handleServicioSelect = (event, newValue) => {
        if (newValue && !serviciosSeleccionados.find(s => s.id === newValue.id)) {
            setServiciosSeleccionados([...serviciosSeleccionados, {
                ...newValue,
                cantidad: 0
            }]);
        }
    };

    const handleServicioChange = (index, field, value) => {
        const updated = [...serviciosSeleccionados];
        updated[index][field] = value;
        setServiciosSeleccionados(updated);
    };

    const handleRemoveServicio = (index) => {
        setServiciosSeleccionados(serviciosSeleccionados.filter((_, i) => i !== index));
    };

    const handleMaterialSelect = (event, newValue) => {
        if (newValue && !materialesSeleccionados.find(m => m.id === newValue.id)) {
            setMaterialesSeleccionados([...materialesSeleccionados, {
                ...newValue,
                cantidad: 0
            }]);
        }
    };

    const handleMaterialChange = (index, field, value) => {
        const updated = [...materialesSeleccionados];
        updated[index][field] = value;
        setMaterialesSeleccionados(updated);
    };

    const handleRemoveMaterial = (index) => {
        setMaterialesSeleccionados(materialesSeleccionados.filter((_, i) => i !== index));
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const validateForm = () => {
        if (!formData.fecha) {
            showSnackbar('Debe seleccionar una fecha', 'warning');
            return false;
        }

        if (!formData.cuadrilla) {
            showSnackbar('Debe seleccionar una cuadrilla', 'warning');
            return false;
        }

        if (!formData.obra) {
            showSnackbar('Debe seleccionar una obra', 'warning');
            return false;
        }

        if (serviciosSeleccionados.length === 0 && materialesSeleccionados.length === 0) {
            showSnackbar('Debe seleccionar al menos un servicio o material', 'warning');
            return false;
        }

        for (let i = 0; i < serviciosSeleccionados.length; i++) {
            const serv = serviciosSeleccionados[i];
            if (!serv.cantidad || serv.cantidad <= 0) {
                showSnackbar(`Debe ingresar una cantidad válida para ${serv.descripcion}`, 'warning');
                return false;
            }
        }

        for (let i = 0; i < materialesSeleccionados.length; i++) {
            const mat = materialesSeleccionados[i];
            if (!mat.cantidad || mat.cantidad <= 0) {
                showSnackbar(`Debe ingresar una cantidad válida para ${mat.descripcion}`, 'warning');
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            await runTransaction(firestore, async (transaction) => {
                const cuadrillaData = cuadrillas.find(c => c.docId === formData.cuadrilla);
                const obraData = obras.find(o => o.docId === formData.obra.docId);
                
                const trabajoRef = doc(collection(firestore, 'trabajos_huawei'));
                const trabajoData = {
                    fecha: formData.fecha.format('DD/MM/YYYY'),
                    timestamp: formData.fecha.valueOf(),
                    cuadrilla: {
                        id: cuadrillaData.id,
                        docId: cuadrillaData.docId,
                        numero: cuadrillaData.numero
                    },
                    obra: {
                        id: obraData.id,
                        docId: obraData.docId,
                        nombre: obraData.nombre
                    },
                    servicios: serviciosSeleccionados.map(s => ({
                        idServicio: s.id,
                        codigo: s.codigo,
                        descripcion: s.descripcion,
                        unidad: s.unidad,
                        cantidad: parseFloat(s.cantidad),
                        zona1: s.zona1,
                        zona2: s.zona2,
                        zona3: s.zona3
                    })),
                    materiales: materialesSeleccionados.map(m => ({
                        idMaterial: m.id,
                        codigo: m.codigo,
                        descripcion: m.descripcion,
                        unidad: m.unidad,
                        cantidad: parseFloat(m.cantidad)
                    })),
                    observaciones: formData.observaciones,
                    createdAt: moment().valueOf()
                };

                transaction.set(trabajoRef, trabajoData);

                // Actualizar el índice cuadrillasAsociadas en la obra
                const obraRef = doc(firestore, 'obras_huawei', formData.obra.docId);
                const obraDoc = await transaction.get(obraRef);
                if (obraDoc.exists()) {
                    const cuadrillasAsociadas = obraDoc.data().cuadrillasAsociadas || {};
                    cuadrillasAsociadas[cuadrillaData.docId] = true;
                    transaction.update(obraRef, { cuadrillasAsociadas });
                }
            });

            showSnackbar('Trabajo ejecutado registrado exitosamente', 'success');
            
            // Resetear formulario
            setFormData({
                fecha: moment(),
                cuadrilla: '',
                obra: null,
                observaciones: ''
            });
            setServiciosSeleccionados([]);
            setMaterialesSeleccionados([]);
            
        } catch (error) {
            console.error('Error al registrar trabajo:', error);
            showSnackbar('Error al registrar el trabajo', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (loading && cuadrillas.length === 0) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Nuevo Trabajo Ejecutado - Huawei
                </Typography>

                <Paper sx={{ p: 3, mt: 3 }}>
                    <Grid container spacing={3}>
                        {/* Fecha */}
                        <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Fecha *"
                                    value={formData.fecha}
                                    onChange={handleDateChange}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    format="DD/MM/YYYY"
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* Cuadrilla */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Cuadrilla *"
                                name="cuadrilla"
                                value={formData.cuadrilla}
                                onChange={handleInputChange}
                            >
                                {cuadrillas.map((cuadrilla) => (
                                    <MenuItem key={cuadrilla.docId} value={cuadrilla.docId}>
                                        {cuadrilla.usuario ? `${cuadrilla.usuario} - ${cuadrilla.numero}` : cuadrilla.numero}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Obra */}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                options={obras}
                                getOptionLabel={(option) => option.nombre || ''}
                                value={formData.obra}
                                onChange={(event, newValue) => {
                                    setFormData(prev => ({ ...prev, obra: newValue }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Obra *"
                                        placeholder="Seleccione una obra"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Observaciones */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Observaciones"
                                name="observaciones"
                                value={formData.observaciones}
                                onChange={handleInputChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Servicios Realizados
                            </Typography>
                        </Grid>

                        {/* Autocomplete de Servicios */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={servicios}
                                getOptionLabel={(option) => `${option.codigo} - ${option.descripcion}`}
                                onChange={handleServicioSelect}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Seleccionar Servicio"
                                        placeholder="Buscar por código o descripción"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Lista de servicios seleccionados */}
                        {serviciosSeleccionados.length > 0 && (
                            <Grid item xs={12}>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Cantidad *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 80 }}>Acción</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {serviciosSeleccionados.map((servicio, index) => (
                                                <TableRow key={`${servicio.id}-${index}`}>
                                                    <TableCell>{servicio.codigo}</TableCell>
                                                    <TableCell>{servicio.descripcion}</TableCell>
                                                    <TableCell>
                                                        <Chip label={servicio.unidad} size="small" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={servicio.cantidad}
                                                            onChange={(e) => handleServicioChange(index, 'cantidad', e.target.value)}
                                                            inputProps={{ min: 0, step: 0.01 }}
                                                            fullWidth
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleRemoveServicio(index)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Materiales Utilizados
                            </Typography>
                        </Grid>

                        {/* Autocomplete de Materiales */}
                        <Grid item xs={12}>
                            <Autocomplete
                                options={materiales}
                                getOptionLabel={(option) => `${option.codigo} - ${option.descripcion}`}
                                onChange={handleMaterialSelect}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Seleccionar Material"
                                        placeholder="Buscar por código o descripción"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Lista de materiales seleccionados */}
                        {materialesSeleccionados.length > 0 && (
                            <Grid item xs={12}>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Unidad</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Cantidad *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: 80 }}>Acción</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {materialesSeleccionados.map((material, index) => (
                                                <TableRow key={`${material.id}-${index}`}>
                                                    <TableCell>{material.codigo}</TableCell>
                                                    <TableCell>{material.descripcion}</TableCell>
                                                    <TableCell>
                                                        <Chip label={material.unidad} size="small" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={material.cantidad}
                                                            onChange={(e) => handleMaterialChange(index, 'cantidad', e.target.value)}
                                                            inputProps={{ min: 0, step: 1 }}
                                                            fullWidth
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleRemoveMaterial(index)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )}

                        {/* Botones de acción */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => window.location.href = '/huawei/trabajos'}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                                >
                                    {loading ? 'Guardando...' : 'Registrar Trabajo'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default AddTrabajo;
